'use client';
/**
 * Chantier manuel-vitrine, lot V6 — le PARAMÉTRAGE du manuel, rapatrié dans
 * la rubrique (décision du 15/08 : par module, comme la doctrine du 21/07 —
 * jamais dans la roue crantée globale, qui règle le module Qualité quand
 * ceci règle un document opposable).
 *
 * Rapatriement ACHEVÉ le 18/08/2026 : listes (avec couleurs et icônes),
 * champs (activation, obligation, type, ordre, sections), affichages,
 * statuts, livret et forfait se règlent tous ici — les éditeurs avancés
 * vivent dans `ParametrageAvance.tsx`. L'écriture passe par le moteur
 * (`ged/manuel-ref/parametrage`), dont la liste blanche EXCLUT toujours les
 * droits (`rolePerms`, `profileMeta`) : ils se règlent dans « Droits par
 * section ». Les boutons « Ouvrir dans l'atelier » ont disparu avec le
 * dernier morceau — ils NOMMAIENT ce qui n'était pas encore porté.
 *
 * ── Couleurs et icônes (18/08/2026) ─────────────────────────────────────────
 *
 * La RQ : « le paramétrage du catalogue était plus beau sur le manuel ; ici il
 * n'y a pas toutes les options de gestion des couleurs ». C'était exact — la
 * première tranche ne portait que l'ajout et le retrait de libellés. Un libellé
 * de liste peut recevoir une COULEUR et une ICÔNE : il s'affiche alors comme
 * une pastille sur les fiches, à la manière du récipient normalisé.
 *
 * Ce n'était pas une recopie possible en l'état : les deux applications ne
 * partagent pas leur couche de composants (le manuel a son magasin `useAppStore`
 * et ses atomes, Qualité a `@/components/ui` et le moteur REST). Ce qui est
 * partagé, c'est ce qui DOIT l'être pour que les deux écrans ne divergent pas :
 * la palette et les tracés d'icônes, dans `@/shared/pastillesListes`. Le reste
 * est réécrit contre le chemin de données de Qualité — et écrit la MÊME donnée
 * (`field-config.listStyles` / `listStyled`), déjà admise par la liste blanche
 * du moteur.
 */
import React, { useEffect, useRef, useState } from 'react';
import { BoutonJournal } from '@/components/BoutonJournal';
import { useRemonteEnHaut, card, EmptyState, Skeleton, useConfirmation } from '@/components/ui';
import { OPTION_COLORS, ICON_PATHS, ICON_CHOICES } from '@/shared/pastillesListes';
import { OngletChamps, OngletAffichages, OngletStatuts, OngletLivret, OngletForfait } from './ParametrageAvance';
import { moteur, OngletCriteresReferentiel } from './Registres';
import { OngletProfilsMateriel } from './ProfilsMateriel';
import { OngletGuidesRecueil } from './GuidesRecueil';
import { OngletLectureCatalogue } from './LectureCatalogue';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
type StyleOption = { color?: string; icon?: string };
type Parametrage = {
  lists: Record<string, string[]> | null;
  /** Style par LIBELLÉ : `{ echantillon: { 'Sérum': { color, icon } } }`. */
  listStyles: Record<string, Record<string, StyleOption>> | null;
  /** Quelles listes s'affichent en pastilles : `{ echantillon: true }`. */
  listStyled: Record<string, boolean> | null;
  activeFields: Record<string, boolean> | null;
  requiredFields: Record<string, boolean> | null;
  /** Qui reçoit chaque champ : `public` | `correspondant` | `laboratoire` | `interne`
   *  (26/08/2026, quatre niveaux depuis le 16/10/2026 ; « confrere » encore lu = laboratoire).
   *  Appliqué CÔTÉ SERVEUR — ce n'est pas un réglage d'affichage. */
  fieldVisibility: Record<string, string> | null;
  ficheCols: number | null;
  livret: Record<string, unknown> | null;
  /* Le reste du sac de configuration, consommé par les éditeurs avancés. Typé
     large : la forme fine vit dans `ParametrageAvance.tsx`, au plus près de
     celui qui s'en sert. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & Record<string, any>;

/** Une icône de pastille. Les tracés viennent du module partagé : Qualité et le
 *  manuel n'ont pas le même composant d'icône, mais ils doivent proposer
 *  exactement le même choix — sinon une icône réglée d'un côté serait
 *  introuvable de l'autre, sans que rien ne le signale. */
function IconePastille({ nom, taille = 14, couleur = 'currentColor' }: { nom: string; taille?: number; couleur?: string }) {
  const trace = ICON_PATHS[nom];
  if (!trace) return null;
  return (
    <svg width={taille} height={taille} viewBox="0 0 24 24" fill="none" stroke={couleur}
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden
      dangerouslySetInnerHTML={{ __html: trace }} />
  );
}

const erreurStyle: React.CSSProperties = { color: 'var(--red-600)', fontSize: 12.5, marginTop: 6 };

const SOUS_ONGLETS_PARAMETRAGE = ['listes', 'champs', 'affichages', 'statuts', 'livret', 'forfait', 'criteres', 'materiels', 'recueil', 'lecture'] as const;
type SousOngletParametrage = typeof SOUS_ONGLETS_PARAMETRAGE[number];

export function OngletParametrage({ estRq }: { estRq: boolean }) {
  const { demander, confirmation } = useConfirmation();
  const [p, setP] = useState<Parametrage | null>(null);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [version, setVersion] = useState(0);
  const [envoi, setEnvoi] = useState(false);
  /* LA VERSION LUE DE CHAQUE CLÉ (14/10/2026). Les onglets avancés gardent un
     brouillon pris à LEUR ouverture ; c'est la version de CETTE lecture qu'il
     faut renvoyer, pas celle d'un rechargement ultérieur — sinon une clé
     changée par un autre entre-temps passerait pour lue. On ne la remplace
     donc que pour les clés qu'on vient d'écrire (réponse du moteur), ou en
     entier après un refus, en remontant les onglets. `null` = moteur antérieur
     qui ne sert pas de versions : on n'en envoie pas (il les refuserait). */
  const versionsLues = useRef<Record<string, string> | null>(null);
  const rechargerOnglets = useRef(false);
  const [generation, setGeneration] = useState(0);
  const [listeOuverte, setListeOuverte] = useState<string | null>(null);
  const [nouvelElement, setNouvelElement] = useState('');
  /* Un seul sélecteur ouvert à la fois — deux panneaux superposés se
     recouvriraient, et l'utilisateur cliquerait dans le mauvais. */
  const [pastilleOuverte, setPastilleOuverte] = useState<string | null>(null);
  /* `?sous=` lu UNE FOIS, à l'ouverture, et validé contre la liste : c'est
     l'adresse qu'ouvre un lien profond (« créez d'abord le profil dans
     Paramétrage → Profils de matériel »). Le relire à chaque rendu ramènerait
     de force sur ce sous-onglet dès qu'on en change. */
  const [sousOnglet, setSousOnglet] = useState<SousOngletParametrage>(() => {
    if (typeof window === 'undefined') return 'listes';
    const voulu = new URLSearchParams(window.location.search).get('sous');
    return (SOUS_ONGLETS_PARAMETRAGE as readonly string[]).includes(voulu || '') ? (voulu as SousOngletParametrage) : 'listes';
  });
  // Un sous-onglet = une arrivée : on remonte (règle du 24/08/2026, cf. le crochet).
  useRemonteEnHaut(sousOnglet);

  useEffect(() => {
    let vivant = true;
    void moteur('manuel-ref/parametrage').then((r) => {
      if (!vivant) return;
      if (!r.ok) { setErreur(r.message); return; }
      const v = r.data.versions;
      if (v && typeof v === 'object' && !versionsLues.current) versionsLues.current = { ...(v as Record<string, string>) };
      setP((r.data.parametrage as unknown as Parametrage) || null);
      if (rechargerOnglets.current) { rechargerOnglets.current = false; setGeneration((g) => g + 1); }
    });
    return () => { vivant = false; };
  }, [version]);

  if (erreur && !p) return <EmptyState titre="Paramétrage indisponible" indice={erreur} />;
  if (!p) return <Skeleton rows={5} />;

  const lists = p.lists || {};
  const styles = p.listStyles || {};
  const styled = p.listStyled || {};

  /* Le moteur écrit LES CLÉS REÇUES et ne touche pas aux autres — mais chaque
     clé reçue est remplacée ENTIÈRE. D'où la règle en vigueur ici : n'envoyer
     que les clés qu'on modifie, et les envoyer COMPLÈTES. Poster
     `listStyles: { echantillon: … }` seul effacerait le style de toutes les
     autres listes, sans erreur et sans que rien ne s'affiche de travers avant
     le prochain chargement du manuel. */
  const ecrire = (corps: Record<string, unknown>, quoi: string) => {
    setEnvoi(true); setMessage(''); setErreur('');
    const lues = versionsLues.current;
    const versions = lues
      ? Object.fromEntries(Object.keys(corps).filter((k) => typeof lues[k] === 'string').map((k) => [k, lues[k]]))
      : null;
    const envoye = versions && Object.keys(versions).length ? { ...corps, versions } : corps;
    void moteur('manuel-ref/parametrage', envoye).then((r) => {
      setEnvoi(false);
      if (r.status === 409) {
        /* Quelqu'un a modifié une de ces clés depuis la lecture : rien n'est
           écrit. On relit TOUT (versions comprises) et l'on remonte les
           onglets, pour que la personne voie la valeur de l'autre avant de
           refaire son geste. */
        setErreur(r.message);
        versionsLues.current = null;
        rechargerOnglets.current = true;
        setVersion((v) => v + 1);
        return;
      }
      if (!r.ok) { setErreur(r.message); return; }
      const nouvelles = r.data.versions;
      if (versionsLues.current && nouvelles && typeof nouvelles === 'object') {
        for (const k of Object.keys(corps)) {
          const n = (nouvelles as Record<string, unknown>)[k];
          if (typeof n === 'string') versionsLues.current[k] = n;
        }
        /* `forfait` s'écrit dans `org` : sa version suit. */
        const o = (nouvelles as Record<string, unknown>).org;
        if ('forfait' in corps && typeof o === 'string') versionsLues.current.org = o;
      }
      setMessage(`${quoi} — les écrans du manuel les liront à leur prochain chargement.`);
      /* ⚠️ PLUS DE `setP(null)` (RQ, 26/08/2026) : « quand j'enregistre quelque
         chose dans la fenêtre de paramétrage, je ne veux pas quitter la page ».
         Vider `p` faisait retomber l'écran sur son squelette (`if (!p) return
         <Skeleton/>`), démontait les sous-onglets, perdait la position de
         lecture et refermait tout éditeur ouvert. On rechargeait pour rien : la
         donnée qu'on venait d'envoyer est déjà celle qu'on a sous les yeux.

         `version` suffit — l'effet relit et REMPLACE `p` quand la réponse
         arrive, sans que l'écran ait clignoté entre-temps. */
      setVersion((v) => v + 1);
    });
  };
  const ecrireListes = (nouvelles: Record<string, string[]>) => ecrire({ lists: nouvelles }, 'Listes enregistrées');

  const styleDe = (nom: string, el: string): StyleOption => styles[nom]?.[el] || {};
  /** Pose (ou efface) la couleur ou l'icône d'un libellé. */
  const poserStyle = (nom: string, el: string, champ: 'color' | 'icon', valeur: string) => {
    const avant = styleDe(nom, el);
    const apres: StyleOption = { ...avant, [champ]: valeur };
    /* Une icône sans couleur ne s'affiche nulle part : la pastille tire sa
       teinte de la couleur, et l'icône se dessine en blanc dessus. Retirer la
       couleur retire donc l'icône — sinon le réglage resterait en base, muet,
       et réapparaîtrait à la première couleur reposée. */
    if (champ === 'color' && !valeur) delete apres.icon;
    if (!apres.color) delete apres.color;
    if (!apres.icon || apres.icon === 'none') delete apres.icon;
    const listeApres = { ...(styles[nom] || {}) };
    if (Object.keys(apres).length) listeApres[el] = apres; else delete listeApres[el];
    setPastilleOuverte(null);
    ecrire({
      listStyles: { ...styles, [nom]: listeApres },
      // Poser une couleur ALLUME l'affichage en pastilles de la liste : sans
      // ce drapeau, la couleur serait enregistrée et n'apparaîtrait nulle part.
      ...(valeur ? { listStyled: { ...styled, [nom]: true } } : {}),
    }, 'Apparence enregistrée');
  };
  /* ── FAMILLE ET SOUS-FAMILLES : PROJETÉES, PLUS SAISIES (RQ, 07/09/2026) ──
     « Fusionner » : la famille du catalogue EST la discipline. Ces listes sont
     recalculées par le moteur depuis Paramétrage → Disciplines (une racine =
     une famille, une fille = une sous-famille) et le moteur refuse d'écrire ce
     qu'on lui enverrait à leur place. Elles restent VISIBLES ici — avec leur
     couleur et leur icône, qui sont bien des réglages d'affichage du catalogue —
     mais on ne les ajoute ni ne les retire d'ici. Même règle que le serveur
     (`familles-catalogue.ts`) ; la répéter à l'écran évite un bouton qui dit
     « enregistré » sans rien enregistrer. */
  const projetee = (nom: string) => nom === 'famille' || nom === 'sousFamille' || nom.startsWith('sousFamille:');
  const ajouter = (nom: string) => {
    if (projetee(nom)) return;
    const v = nouvelElement.trim();
    if (!v) return;
    ecrireListes({ ...lists, [nom]: [...(lists[nom] || []), v] });
    setNouvelElement('');
  };
  const retirer = async (nom: string, element: string) => {
    if (projetee(nom)) return;
    /* `useConfirmation` et non plus `window.confirm` (24/08/2026) : la boîte
       native bloque le fil du navigateur, ignore Échap-sur-fond et ne
       ressemble à rien d'autre dans l'application. */
    if (!(await demander(`Retirer « ${element} » de la liste « ${nom} » ?\nLes fiches qui portent déjà cette valeur la GARDENT — la retirer ferme seulement la saisie future.`,
      { confirmer: 'Retirer' }))) return;
    ecrireListes({ ...lists, [nom]: (lists[nom] || []).filter((x) => x !== element) });
  };

  return (
    <div>
      {confirmation}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {/* Ce qui est tracé se VOIT (24/08/2026). */}
        <BoutonJournal reference="MAN-PARAMETRAGE" quoi="du paramétrage du manuel" />
      </div>
      {message && <div style={{ color: 'var(--green-600)', fontSize: 12.5, marginBottom: 6 }}>{message}</div>}
      {erreur && <div style={erreurStyle}>{erreur}</div>}

      {/* Un sous-onglet par famille de réglages : tout dans une page unique,
          c'était l'écran où la RQ se perdait. */}
      <div style={{ margin: '0 0 10px' }}>
        <Segment ariaLabel="Section" valeur={sousOnglet} onChange={setSousOnglet} options={([['listes', 'Listes'], ['champs', 'Champs'], ['affichages', 'Affichages'],
          ['statuts', 'Statuts'], ['criteres', 'Critères d’échantillon'], ['materiels', 'Profils de matériel'],
          /* Les guides de recueil (13/09/2026) : ce qui s'affiche en encart sur
             la fiche d'examen, et sur quels examens. */
          ['recueil', 'Recueil'],
          /* Qui voit quoi dans le catalogue (14/09/2026). */
          ['lecture', 'Lecture du catalogue'],
          ['livret', 'Livret'], ['forfait', 'Forfait']] as const).map(([v, l]) => ({ valeur: v, libelle: l }))} />
      </div>

      {sousOnglet === 'lecture' && <OngletLectureCatalogue estRq={estRq} parametres={p.lectureCatalogue ?? null} />}

      {sousOnglet === 'recueil' && (
        <OngletGuidesRecueil estRq={estRq} parametres={p.guidesRecueil ?? null} manualContent={(p.manualContent as Record<string, string>) || null} />
      )}

      {sousOnglet === 'listes' && (
      <div style={{ ...card, padding: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>
          Listes du catalogue
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 8 }}>
          Donnez à un libellé une <strong>couleur</strong> et une <strong>icône</strong> : il s’affichera
          en pastille sur les fiches, comme le récipient normalisé. La ligne de réglage montre le résultat
          tel qu’il paraîtra. Retirer un élément ne touche pas les fiches qui le portent : cela ferme
          seulement la saisie future. Les droits (qui propose, qui valide) ne se règlent pas ici —
          ils sont dans « Rôles & accès ».
        </div>
        {Object.keys(lists).sort().map((nom) => (
          <div key={nom} style={{ marginBottom: 8 }}>
            <Bouton variante={listeOuverte === nom ? 'primaire' : 'contour'} taille="sm" onClick={() => { setListeOuverte(listeOuverte === nom ? null : nom); setNouvelElement(''); }}>
              {nom} ({(lists[nom] || []).length})
            </Bouton>
            {projetee(nom) && (
              <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                projetée depuis <strong>Paramétrage → Disciplines</strong> — s’y modifie
              </span>
            )}
            {listeOuverte === nom && (
              <div style={{ margin: '6px 0 0 12px', fontSize: 12.5 }}>
                {(lists[nom] || []).map((el) => {
                  const st = styleDe(nom, el);
                  const couleur = st.color || '';
                  const icone = couleur ? (st.icon || 'none') : 'none';
                  const cle = `${nom}::${el}`;
                  return (
                    <div key={el} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', maxWidth: 560, padding: '4px 0' }}>
                      {/* Le libellé TEL QU'IL PARAÎTRA sur la fiche : la ligne
                          de réglage est son propre aperçu. Un nuancier séparé
                          oblige à imaginer le résultat. */}
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 150,
                        padding: '3px 11px', borderRadius: 'var(--radius-pill)', fontSize: 12.5, fontWeight: 600,
                        color: couleur ? 'var(--surface-card)' : 'var(--text-body)',
                        background: couleur || 'transparent',
                        border: `1px solid ${couleur || 'var(--border-default)'}`,
                      }}>
                        {icone !== 'none' && <IconePastille nom={icone} taille={13} couleur="#fff" />}
                        {el}
                      </span>

                      {estRq && (
                        <>
                          {/* Couleur */}
                          <div style={{ position: 'relative' }}>
                            <Bouton taille="sm"
                              disabled={envoi} title="Choisir la couleur"
                              onClick={() => setPastilleOuverte(pastilleOuverte === `c:${cle}` ? null : `c:${cle}`)}>
                              <span style={{ width: 14, height: 14, borderRadius: 'var(--radius-xs)', background: couleur || 'transparent', border: `1.5px solid ${couleur || 'var(--border-strong)'}` }} />
                              Couleur
                            </Bouton>
                            {pastilleOuverte === `c:${cle}` && (
                              <div style={{ position: 'absolute', zIndex: 95, top: 32, left: 0, padding: 10, background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-xl)' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 24px)', gap: 7 }}>
                                  {OPTION_COLORS.map((c) => (
                                    <button key={c} title={c} onClick={() => poserStyle(nom, el, 'color', c)}
                                      style={{ width: 24, height: 24, borderRadius: 'var(--radius-sm)', background: c, cursor: 'pointer', padding: 0, border: `2px solid ${couleur === c ? 'var(--navy-900)' : 'rgba(14,39,64,0.15)'}` }} />
                                  ))}
                                </div>
                                <Bouton taille="sm" onClick={() => poserStyle(nom, el, 'color', '')} style={{ width: '100%', marginTop: 9 }}>Sans couleur</Bouton>
                              </div>
                            )}
                          </div>

                          {/* Icône — sans couleur, elle n'aurait rien sur quoi se poser. */}
                          <div style={{ position: 'relative' }}>
                            <Bouton taille="sm"
                              disabled={envoi || !couleur}
                              title={couleur ? 'Choisir l’icône' : 'Choisissez d’abord une couleur : l’icône se dessine sur la pastille.'}
                              onClick={() => setPastilleOuverte(pastilleOuverte === `i:${cle}` ? null : `i:${cle}`)}>
                              <span style={{ display: 'inline-flex', width: 15, height: 15, alignItems: 'center', justifyContent: 'center' }}>
                                {icone !== 'none' && <IconePastille nom={icone} taille={14} couleur="var(--text-strong)" />}
                              </span>
                              Icône
                            </Bouton>
                            {pastilleOuverte === `i:${cle}` && (
                              <div style={{ position: 'absolute', zIndex: 95, top: 32, left: 0, padding: 10, background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-xl)' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 30px)', gap: 6 }}>
                                  {ICON_CHOICES.map((ic) => {
                                    const actif = ic === icone;
                                    return (
                                      <button key={ic} title={ic === 'none' ? 'Aucune' : ic} onClick={() => poserStyle(nom, el, 'icon', ic)}
                                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 'var(--radius-sm)', cursor: 'pointer', padding: 0, background: actif ? 'var(--navy-900)' : 'var(--surface-card)', border: `1.5px solid ${actif ? 'var(--navy-900)' : 'var(--border-default)'}` }}>
                                        {ic === 'none'
                                          ? <span style={{ fontSize: 15, lineHeight: 1, color: 'var(--text-faint)' }}>—</span>
                                          : <IconePastille nom={ic} taille={16} couleur={actif ? 'var(--surface-card)' : 'var(--navy-900)'} />}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>

                          {!projetee(nom) && (
                            <Bouton taille="sm" disabled={envoi} onClick={() => void retirer(nom, el)} style={{ marginLeft: 'auto' }}>Retirer</Bouton>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
                {estRq && !projetee(nom) && (
                  <div style={{ marginTop: 6 }}>
                    <input value={nouvelElement} placeholder="Nouvel élément…"
                      onChange={(e) => setNouvelElement(e.currentTarget.value)} style={{ width: 260 }} />
                    <Bouton variante="primaire" taille="sm" disabled={envoi || !nouvelElement.trim()} onClick={() => ajouter(nom)} style={{ marginLeft: 6 }}>Ajouter</Bouton>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {!Object.keys(lists).length && <EmptyState titre="Aucune liste" indice="Le seed du paramétrage ne s’est jamais joué." />}
      </div>
      )}

      {/* `key={generation}` : après un refus pour version périmée, les onglets
          repartent de la lecture à jour au lieu de garder leur brouillon. */}
      {sousOnglet === 'champs' && <OngletChamps key={`champs-${generation}`} p={p} estRq={estRq} envoi={envoi} ecrire={ecrire} />}
      {sousOnglet === 'affichages' && <OngletAffichages key={`affichages-${generation}`} p={p} estRq={estRq} envoi={envoi} ecrire={ecrire} />}
      {sousOnglet === 'statuts' && <OngletStatuts key={`statuts-${generation}`} p={p} estRq={estRq} envoi={envoi} ecrire={ecrire} />}
      {sousOnglet === 'livret' && <OngletLivret key={`livret-${generation}`} p={p} estRq={estRq} envoi={envoi} ecrire={ecrire} />}
      {sousOnglet === 'forfait' && <OngletForfait key={`forfait-${generation}`} p={p} estRq={estRq} envoi={envoi} ecrire={ecrire} />}
      {/* Les critères d'acceptation d'échantillon viennent de l'onglet « Autres
          registres », retiré le 19/08 : douze critères qui décrivent quand on
          REFUSE un prélèvement, c'est un réglage du catalogue, pas un registre
          à part. Ils portent 4788 liaisons vers les fiches et s'affichent sur
          la fiche publique — les supprimer aurait vidé ce que la fiche dit du
          refus. Ils changent de place, pas de statut. */}
      {sousOnglet === 'criteres' && <OngletCriteresReferentiel estRq={estRq} />}
      {/* 07/09/2026 — matière × état : les conditions vivent ici, les examens s'y réfèrent. */}
      {sousOnglet === 'materiels' && <OngletProfilsMateriel estRq={estRq} />}
    </div>
  );
}
